import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import dotProp from "dot-prop-immutable"
import _ from "lodash";

import { useFirebase } from '@meddbriefer/mdb-firebase';
import { getScenarioByID } from "@meddbriefer/scenario-data"

import { useScenarioEditor } from './ScenarioEditorContext';
import ScenarioEditorLayout from "./ScenarioEditorLayout";


import { applyFieldDefaultValue, trimFieldValue, ignoreTrailingSpaceInStrings } from "./formUtils";

const VITALS = ["BP", "P", "R", "Skin", "Spo2", "ETCO2", "GCS", "Glucose", "Pain", "Temp"]
const TYPES = ["initial", "good", "bad"]
const SAMPLE = ["S", "A", "M", "P", "L", "E"]
const OPQRST = ["O", "P", "Q", "R", "S", "T"]


export default function PatientStatus() {
    const {db} = useFirebase()
    const {scenarioId} = useParams()
    const {scenario, updateScenarioFld, saveScenario} = useScenarioEditor()
    const [state, setState] = useState(null)
    const [origState, setOrigState] = useState(null) // so we can compare orig to current efficiently
    const [hasChanges, setHasChanges] = useState(false) // if 'true', our save btn will be enabled
    const [stateUpdated, setStateUpdated] = useState(false) // if 'true', a useEffect() determines the value of hasChanges
    const [readyToSave, setReadyToSave] = useState(false) // set to 'true' after field values have been cleaned

    const cleanFieldValues = () => {
        let cleanedValues = {...state}

        VITALS.forEach(vital => {
            TYPES.forEach(typ => {
                const fldPath = `vitalSigns.${vital}.${typ}`
                cleanedValues = applyFieldDefaultValue(cleanedValues, fldPath, "Unable to assess")
                cleanedValues = trimFieldValue(cleanedValues, fldPath)
            })
        })
        SAMPLE.forEach(fld => {
            const fldPath = `SAMPLE.${fld}`
            cleanedValues = applyFieldDefaultValue(cleanedValues, fldPath, 'unknown')
            cleanedValues = trimFieldValue(cleanedValues, fldPath)
        })
        OPQRST.forEach(fld => {
            const fldPath = `OPQRST.${fld}`
            cleanedValues = applyFieldDefaultValue(cleanedValues, fldPath, "unable to assess")
            cleanedValues = trimFieldValue(cleanedValues, fldPath)
        })
        if (!_.isEqual(state, cleanedValues)) {
            console.log("some fields were cleaned. updating state")
            //update all changes in one fell swoop
            setState(cleanedValues)
        }
    }

    const saveHandler = (event) => {
        cleanFieldValues()
        setReadyToSave(true)
    }

    const updateState = (fldPath, value) => {
        setState(dotProp.set(state, fldPath, value))
        setStateUpdated(true)
    }

    const changeHandler = (event) => {
		const {id: fldPath, value} = event.target
		updateState(fldPath, value)
	}

    useEffect(
        () => {
            // *should* only run on initial load
            if (!state) {
                const tmp = {
                    vitalSigns: scenario.vitalSigns,
                    SAMPLE: scenario.SAMPLE,
                    OPQRST: scenario.OPQRST
                }
                // unlike 'state', 'origState' only gets updated hear and when saving. It's purpose is to allow for
                // comparison (hasChanges)
                setOrigState(tmp)
                setState(tmp)
            }
        }, [scenarioId, scenario, state]
    )

    useEffect(
        () => {
            if (stateUpdated) {
                // hasChanges may change between true and false as we make changes/set things back to orig values
                const hc = ! _.isEqualWith(origState, state, ignoreTrailingSpaceInStrings)
                setHasChanges(hc)
                console.log("state has changes", hc)
                setStateUpdated(false)
            }
        }, [stateUpdated, origState, state]
    )

    useEffect(
        () => {
            if (readyToSave) {
                // saveHandler triggers the cleaning of field values, and then we end up here

                // set to false immediately so we don't end up in an endless loop and use all of our free firebase time
                setReadyToSave(false)

                // update the scenario object with our local state. the question is, should we be waiting until
                // now to do this.
                updateScenarioFld("vitalSigns", state.vitalSigns)
                updateScenarioFld("SAMPLE", state.SAMPLE)
                updateScenarioFld("OPQRST", state.OPQRST)

                // set the following so that we no longer have differences between origState and state
                setOrigState(state)
                setHasChanges(false)

                const saveFunc = async () => {
                    // only persist this forms local state changes to the db
                    // FIXME: I should update saveScenario() so that it's param
                    // is an object of only fields to update, and have it handle this noise
                    const scenData = {
                        ...await getScenarioByID(db, scenarioId),
                        vitalSigns: state.vitalSigns,
                        SAMPLE: state.SAMPLE,
                        OPQRST: state.OPQRST
                    }
                    await saveScenario(scenData)
                }
                saveFunc()
            }
        }, [db, readyToSave, saveScenario, updateScenarioFld, state, scenarioId]
    )

    if (!state) {
        return (<div>loading...</div>)
    }
    return (
        <ScenarioEditorLayout
            label="Save changes to Patient Status"
            haveChanges={hasChanges}
            saveHandler={saveHandler}
        >
            <div className="info-box vitals">
                <h2>Vital Signs</h2>
                <table>
                    <thead>
                        <tr>
                            <th>Vital</th>
                            <th>Initial Value</th>
                            <th>Good Reassessment Value</th>
                            <th>Bad Reassessment Value</th>
                        </tr>
                    </thead>
                    <tbody>
                        {VITALS.map(vital =>
                        <tr key={vital}>
                            <td>{vital}</td>
                            {TYPES.map(typ =>
                            <td key={`${vital}-${typ}`}>
                                <input
                                    id={`vitalSigns.${vital}.${typ}`}
                                    type="text"
                                    value={dotProp.get(state, `vitalSigns.${vital}.${typ}`)}
                                    onChange={changeHandler}
                                />
                            </td>
                            )}
                        </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div style={{ display: "flex" }}>
                <div style={{ flex: 1, width: "100%" }}>
                    <div className="info-wrapper" style={{width: "100%"}}>
                        <h2>SAMPLE</h2>
                        <table style={{width: "100%"}}>
                            <tbody>
                                {SAMPLE.map(fld =>
                                    <tr key={`SAMPLE-${fld}`}>
                                        <td>{fld}</td>
                                        <td>
                                            <input
                                                id={`SAMPLE.${fld}`}
                                                type="text"
                                                size="80"
                                                value={dotProp.get(state, `SAMPLE.${fld}`)}
                                                onChange={changeHandler}
                                            />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div style={{ flex: 1 }}>
                    <div className="info-wrapper">
                        <h2>OPQRST</h2>
                        <table>
                            <tbody>
                                {OPQRST.map(fld =>
                                <tr key={`OPQRST-${fld}`}>
                                    <td>{fld}</td>
                                    <td>
                                        <input
                                            id={`OPQRST.${fld}`}
                                            type="text"
                                            size="80"
                                            value={dotProp.get(state, `OPQRST.${fld}`)}
                                            onChange={changeHandler}
                                        />
                                    </td>
                                </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </ScenarioEditorLayout>

    )
}